<script setup lang="ts">
import type {
  EveMessage,
  EveMessagePart,
} from 'eve/vue'

import { Comark } from '@comark/vue'
import prettyBytes from 'pretty-bytes'
import { UiTextarea } from '#components'
import emoji from 'comark/plugins/emoji'
import { activeTurn, turns } from 'eve/events'
import security from 'comark/plugins/security'

import AppDocueAgentLink from './AppDocueAgentLink.vue'
import AppDocueAgentInputRequest from './AppDocueAgentInputRequest.vue'
import AppDocueAgentToolConfirmation from './AppDocueAgentToolConfirmation.vue'

const props = defineProps<{
  fullScreen?: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

const markdownComponents = {
  a: AppDocueAgentLink,
}
const markdownPlugins = [
  emoji(),
  security({
    allowedTags: [
      'a',
      'blockquote',
      'br',
      'code',
      'del',
      'em',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'hr',
      'img',
      'input',
      'li',
      'ol',
      'p',
      'pre',
      'span',
      'strong',
      'table',
      'tbody',
      'td',
      'th',
      'thead',
      'tr',
      'ul',
    ],
    allowedProtocols: ['docue', 'http', 'https', 'mailto', 'tel'],
    allowDataImages: false,
  }),
]

const { t } = useI18n()
const { locale } = useIntl()
const { activeWorkspaceId } = useAuth()
const { height: windowHeight } = useWindowSize()
const {
  cancel,
  clear,
  messages,
  view,
  hasActiveTurn,
  prewarm,
  error,
  events,
  excludePageContext,
  includePageContext,
  isBusy,
  isPageContextIncluded,
  isResuming,
  pageContext,
  pageContextForMessage,
  platformAgentUrl,
  respond,
  send,
} = useDocueAgent()

const MIN_HEIGHT = 288
const DEFAULT_HEIGHT = 640

const preferredHeight = useSessionStorage('docue-agent-chat-height', DEFAULT_HEIGHT)
const isResizing = ref(false)

const maxHeight = computed(() => Math.round(windowHeight.value * 0.9))
const height = computed(() => clampHeight(preferredHeight.value))

const draft = useSessionStorage(
  () => `docue-agent-chat-draft:${activeWorkspaceId.value ?? ''}`,
  '',
)
const draftInput = useTemplateRef<InstanceType<typeof UiTextarea>>('draftInput')
const draftInputElement = computed(() => draftInput.value?.element ?? undefined)
const chatPanel = useTemplateRef<HTMLElement>('chatPanel')

/** In-memory only — never persisted (Phase 1: no attachment bytes in storage). */
const pendingAttachments = ref<DocueAgentPendingAttachment[]>([])
const attachmentError = ref<string>()
/** The turn currently being submitted, used to render Eve's optimistic echo. */
const inFlightTurn = ref<{
  attachments: DocueAgentPendingAttachment[]
  text: string
}>()
const {
  open: openAttachmentDialog,
  onChange: onAttachmentFilesChange,
  reset: resetAttachmentDialog,
} = useFileDialog({
  accept: DOCUE_AGENT_ATTACHMENT_ACCEPT,
  multiple: true,
})

// The full-screen sheet is only rendered on touch layouts, where icon controls
// need a comfortable hit area. Starter pills stay compact so they don't dominate
// the empty state.
const buttonSize = computed(() => props.fullScreen ? 'lg' : 'xs')
const starterButtonSize = computed(() => props.fullScreen ? 'sm' : 'xs')

const canClear = computed(() => messages.value.length > 0)

interface DisplayAttachment {
  filename?: string
  mediaType: string
  size?: number
}

interface DisplayMessage {
  attachments: DisplayAttachment[]
  durationSeconds?: number
  feedbackTraceId?: string
  id: string
  inputPrompts: ReturnType<typeof docueAgentMessageInputPrompts>
  isWorking: boolean
  pageContext?: SentDocueAgentPageContext
  parts: readonly EveMessagePart[]
  role: 'user' | 'assistant'
  startedAt?: number
  text: string
  textBlocks: DocueAgentTextBlock[]
}

function messageAttachments(message: EveMessage): DisplayAttachment[] {
  return message.parts
    .filter((part): part is Extract<EveMessagePart, { type: 'file' }> => part.type === 'file')
    .map(part => ({
      filename: part.filename,
      mediaType: part.mediaType,
      size: part.size,
    }))
}

/**
 * Eve's optimistic echo flattens file parts into a `[file: name]` text summary,
 * so render the turn we submitted instead of parsing that summary back apart.
 */
function displayContent(message: EveMessage): {
  attachments: DisplayAttachment[]
  text: string
  textBlocks: DocueAgentTextBlock[]
} {
  const attachments = messageAttachments(message)
  if (attachments.length > 0 || !message.metadata?.optimistic || !inFlightTurn.value) {
    const textBlocks = docueAgentMessageTextBlocks(message)
    return {
      attachments,
      text: textBlocks.map(block => block.text).join('\n\n'),
      textBlocks,
    }
  }

  return {
    attachments: inFlightTurn.value.attachments.map(attachment => ({
      filename: attachment.name,
      mediaType: attachment.mediaType,
      size: attachment.size,
    })),
    text: inFlightTurn.value.text,
    textBlocks: [],
  }
}

const turnState = computed(() => {
  const startedAt = new Map<string, number>()
  const durations = new Map<string, number>()
  const traceIds = new Map<string, string>()

  for (const turn of turns(view.value)) {
    const started = Date.parse(turn.startedAt ?? '')
    const ended = Date.parse(turn.endedAt ?? '')
    if (Number.isFinite(started)) {
      startedAt.set(turn.turnId, started)
      if (turn.outcome === 'completed' && Number.isFinite(ended))
        durations.set(turn.turnId, Math.max(1, Math.round((ended - started) / 1000)))
    }
  }
  for (const event of events.value) {
    if (event.type === 'turn.started' && event.data.trace)
      traceIds.set(event.data.turnId, event.data.trace.traceId)
  }

  return { activeTurnId: activeTurn(view.value)?.turnId, durations, startedAt, traceIds }
})

const turnFailure = computed(() => docueAgentTurnFailure(events.value))

const legalResearch = computed(() => docueAgentLegalResearchState(view.value))
const legalResearchStatus = computed(() => legalResearch.value.status)
const legalResearchStatuses = computed(() => legalResearch.value.statuses)
const legalResearchInset = computed(() => `${4 + Math.max(0, legalResearchStatuses.value.length - 1) * 2.25}rem`)
// The research panel already shows progress for the turn waiting on it.
const isLegalResearchTurnBusy = computed(() => !!legalResearchStatus.value)

const displayMessages = computed<DisplayMessage[]>(() => {
  const { activeTurnId, durations, startedAt, traceIds } = turnState.value

  // A rejected optimistic send is restored to the composer below.
  const visibleMessages = messages.value
    .filter(message => !(message.metadata?.optimistic && message.metadata.status === 'failed'))

  return visibleMessages
    .map((message, index) => {
      const turnId = message.metadata?.turnId
      const inputPrompts = docueAgentMessageInputPrompts(message)
      const hasPendingInput = inputPrompts.some(prompt => prompt.pending)
      const { attachments, text, textBlocks } = displayContent(message)
      const hasLaterSteering = hasLaterSteeringMessage(visibleMessages, index, activeTurnId)
      const durationSeconds = turnId ? durations.get(turnId) : undefined

      return {
        attachments,
        durationSeconds,
        // Only a finished assistant answer with no pending prompt is ratable.
        feedbackTraceId: message.role === 'assistant'
          && durationSeconds !== undefined
          && text.trim().length > 0
          && inputPrompts.length === 0
          ? traceIds.get(turnId ?? '')
          : undefined,
        id: message.id,
        inputPrompts,
        isWorking: !isResuming.value
          && !isLegalResearchTurnBusy.value
          && message.role === 'assistant'
          && turnId === activeTurnId
          && !hasLaterSteering
          && !hasPendingInput,
        pageContext: pageContextForMessage(message),
        parts: message.parts,
        role: message.role,
        startedAt: turnId ? startedAt.get(turnId) : undefined,
        text,
        textBlocks,
      }
    })
    .filter(message =>
      message.text.trim().length > 0
      || message.attachments.length > 0
      || message.isWorking
      || message.durationSeconds !== undefined
      || message.inputPrompts.length > 0,
    )
})

const pendingInputPrompts = computed(() =>
  displayMessages.value.flatMap(message =>
    message.inputPrompts.filter(prompt => prompt.pending),
  ),
)

const freeformInputRequest = computed(() =>
  pendingInputPrompts.value.find(({ request }) =>
    request.allowFreeform
    || request.display === 'text'
    || (request.display !== 'confirmation' && !request.options?.length),
  ),
)

const latestSentPageContext = computed(() => {
  for (let index = displayMessages.value.length - 1; index >= 0; index--) {
    const context = displayMessages.value[index]?.pageContext
    if (context)
      return context
  }

  return undefined
})

const isCurrentPageContextLatest = computed(() => {
  const currentContext = pageContext.value
  const latestContext = latestSentPageContext.value
  if (!currentContext || !latestContext)
    return false

  if (!latestContext.id)
    return latestContext.label === currentContext.label

  return latestContext.id === currentContext.id
    && latestContext.payloadKey === getDocueAgentPageContextPayloadKey(currentContext)
})

const canUsePageContext = computed(() =>
  !!pageContext.value
  && !freeformInputRequest.value
  && !isCurrentPageContextLatest.value,
)

const activePageContext = computed(() =>
  canUsePageContext.value && isPageContextIncluded.value
    ? pageContext.value
    : undefined,
)

const hasComposerChips = computed(() =>
  !!activePageContext.value || pendingAttachments.value.length > 0,
)

const canAttach = computed(() =>
  !freeformInputRequest.value
  && !isResuming.value
  && !!platformAgentUrl.value,
)

const hasComposerSubmission = computed(() =>
  !!draft.value.trim()
  || (!freeformInputRequest.value && pendingAttachments.value.length > 0),
)

const canSend = computed(() =>
  !isResuming.value && hasComposerSubmission.value && !!platformAgentUrl.value,
)

const showStop = computed(() =>
  !isResuming.value
  && !hasComposerSubmission.value
  && hasActiveTurn.value,
)

const hasWorkingAssistantMessage = computed(() =>
  displayMessages.value.some(message => message.isWorking),
)

// A prompt parks the open turn; the prompt itself is the activity to show.
const showGenericActivity = computed(() =>
  isBusy.value
  && !hasWorkingAssistantMessage.value
  && !isLegalResearchTurnBusy.value
  && pendingInputPrompts.value.length === 0,
)

const activeTurnStartedAt = computed(() => {
  const { activeTurnId, startedAt } = turnState.value
  return activeTurnId ? startedAt.get(activeTurnId) : undefined
})

const draftPlaceholder = computed(() =>
  freeformInputRequest.value
    ? t('docueAgent.question.answerPlaceholder')
    : t('docueAgent.placeholder'),
)

const showStarters = computed(() =>
  displayMessages.value.length === 0
  && !isBusy.value
  && !isResuming.value
  && !error.value
  && !!platformAgentUrl.value
  && pendingAttachments.value.length === 0,
)

const starters = computed(() => ([
  {
    id: 'documents',
    icon: 'docue:file-search-01',
    label: t('docueAgent.starters.documents'),
    prompt: t('docueAgent.starters.prompts.documents'),
  },
  {
    id: 'actions',
    icon: 'docue:lightning-01',
    label: t('docueAgent.starters.actions'),
    prompt: t('docueAgent.starters.prompts.actions'),
  },
  {
    id: 'features',
    icon: 'docue:info-circle',
    label: t('docueAgent.starters.features'),
    prompt: t('docueAgent.starters.prompts.features'),
  },
  {
    id: 'help',
    icon: 'docue:help-circle',
    label: t('docueAgent.starters.help'),
    prompt: t('docueAgent.starters.prompts.help'),
  },
  {
    id: 'support',
    icon: 'docue:message-chat-circle',
    label: t('docueAgent.starters.support'),
    prompt: t('docueAgent.starters.prompts.support'),
  },
] as const))

watch(draft, (value, previous) => {
  if (value.trim() && !previous.trim() && platformAgentUrl.value && !isResuming.value && !hasActiveTurn.value)
    void prewarm().catch(() => {})
})

// A send is accepted before its turn consumes it. Restore a failed optimistic
// submission even when cancellation or failure arrives after HTTP acceptance.
watch(messages, (value) => {
  const latest = value.findLast(message => message.role === 'user')
  if (latest?.metadata?.optimistic && latest.metadata.status === 'failed' && inFlightTurn.value) {
    if (!draft.value.trim()) {
      draft.value = inFlightTurn.value.text
      pendingAttachments.value = inFlightTurn.value.attachments
    }
    inFlightTurn.value = undefined
  }
})

useTextareaAutosize({
  element: draftInputElement,
  input: draft,
})

function resetAttachments() {
  pendingAttachments.value = []
  attachmentError.value = undefined
}

watch(activeWorkspaceId, () => {
  resetAttachments()
})

async function addAttachmentFiles(files: readonly File[]) {
  if (!files.length || !canAttach.value)
    return

  attachmentError.value = undefined
  // Validated one at a time: the count and aggregate-size limits both depend on
  // the files already accepted, and the error must name the first offender.
  const accepted: DocueAgentPendingAttachment[] = []
  for (const file of files) {
    const validationError = await validateDocueAgentAttachmentFile(
      file,
      [...pendingAttachments.value, ...accepted],
    )
    if (validationError) {
      attachmentError.value = formatAttachmentValidationError(validationError)
      break
    }

    accepted.push(await createDocueAgentPendingAttachment(file))
  }

  if (accepted.length)
    pendingAttachments.value = [...pendingAttachments.value, ...accepted]

  focusDraftInput()
}

function onComposerPaste(event: ClipboardEvent) {
  const files = Array.from(event.clipboardData?.files ?? [])
  if (!files.length || !canAttach.value || event.clipboardData?.getData('text/plain'))
    return

  event.preventDefault()
  void addAttachmentFiles(files)
}

onAttachmentFilesChange(async (fileList) => {
  if (!fileList?.length)
    return

  // Snapshot before reset — clearing the input empties the live FileList.
  const selected = Array.from(fileList)
  resetAttachmentDialog()
  await addAttachmentFiles(selected)
})

const { isOverDropZone } = useDropZone(chatPanel, {
  multiple: true,
  onDrop(files) {
    if (!files?.length)
      return

    void addAttachmentFiles(files)
  },
})

function formatAttachmentValidationError(
  error: DocueAgentAttachmentValidationError,
): string {
  switch (error.code) {
    case 'unsupported-type':
      return t('docueAgent.attachments.errors.unsupportedType', { name: error.name })
    case 'too-many':
      return t('docueAgent.attachments.errors.tooMany', { limit: error.limit })
    case 'too-large':
      return t('docueAgent.attachments.errors.tooLarge', {
        name: error.name,
        limit: formatAttachmentLimit(error.limit),
      })
    case 'aggregate-too-large':
      return t('docueAgent.attachments.errors.aggregateTooLarge', {
        limit: formatAttachmentLimit(error.limit),
      })
    case 'invalid-encoding':
      return t('docueAgent.attachments.errors.invalidEncoding', { name: error.name })
  }
}

function formatAttachmentLimit(bytes: number) {
  return prettyBytes(bytes, { locale: locale.value, maximumFractionDigits: 0 })
}

function removePendingAttachment(id: string) {
  pendingAttachments.value = pendingAttachments.value.filter(
    attachment => attachment.id !== id,
  )
  attachmentError.value = undefined
  focusDraftInput()
}

function clampHeight(value: number) {
  return Math.min(Math.max(value, MIN_HEIGHT), maxHeight.value)
}

function focusDraftInput() {
  // Safari reveals a focused field by scrolling the viewport, which drags the
  // surrounding fixed panel out of view. The field is already visible, so opt out.
  draftInputElement.value?.focus({ preventScroll: true })
}

defineExpose({
  focusDraftInput,
})

function startResize(event: PointerEvent) {
  const startY = event.clientY
  const startHeight = height.value
  isResizing.value = true

  const onPointerMove = (moveEvent: PointerEvent) => {
    preferredHeight.value = clampHeight(startHeight + (startY - moveEvent.clientY))
  }

  const stopResize = useEventListener(document, 'pointermove', onPointerMove)
  const stopPointerUp = useEventListener(document, 'pointerup', () => {
    stopResize()
    stopPointerUp()
    isResizing.value = false
  })
}

async function sendText(text: string, attachments: DocueAgentPendingAttachment[] = []) {
  const message = text.trim()
  if (isResuming.value || !platformAgentUrl.value)
    return

  // Attachments only ride along with a new turn, never with an input response.
  const freeformRequest = freeformInputRequest.value
  if (!message && (freeformRequest || attachments.length === 0))
    return

  const restorePendingMessage = () => {
    draft.value = message
    inFlightTurn.value = undefined
    if (!freeformRequest)
      pendingAttachments.value = attachments
  }

  draft.value = ''
  attachmentError.value = undefined
  if (!freeformRequest) {
    pendingAttachments.value = []
    inFlightTurn.value = { attachments, text: message }
  }
  await nextTick()
  focusDraftInput()

  const initialEventCount = events.value.length
  let sendFailed = false

  try {
    if (freeformRequest) {
      await respond([{
        interactionId: freeformRequest.interactionId,
        text: message,
      }])
    }
    else {
      await send(
        buildDocueAgentUserContent(message, attachments),
        {
          showPageContextReceipt: canUsePageContext.value && isPageContextIncluded.value,
          ...(hasActiveTurn.value ? { turnPolicy: 'steer' as const } : {}),
        },
      )
    }
    sendFailed = !freeformRequest && !!error.value
  }
  catch {
    sendFailed = true
  }

  const messageWasReceived = events.value
    .slice(initialEventCount)
    .some(event => event.type === 'delivery.consumed')
  if (sendFailed && (freeformRequest || !messageWasReceived))
    restorePendingMessage()

  await nextTick()
  focusDraftInput()
}

async function sendMessage() {
  await sendText(draft.value, pendingAttachments.value)
}

function submitOnEnter(event: KeyboardEvent) {
  // On touch keyboards Enter is the only way to start a new line, so the sheet relies
  // on its send button instead.
  if (props.fullScreen || event.isComposing)
    return

  event.preventDefault()
  void sendMessage()
}

async function answerInputRequest(response: {
  optionId?: string
  interactionId: string
  text?: string
}) {
  if (isResuming.value || !platformAgentUrl.value
    || !pendingInputPrompts.value.some(prompt => prompt.interactionId === response.interactionId)) {
    return
  }

  try {
    await respond([response])
  }
  catch {
    // Keep the pending prompt visible so the user can retry.
  }
  await nextTick()
  focusDraftInput()
}

function fillStarter(prompt: string) {
  draft.value = prompt
  focusDraftInput()
}

function togglePageContext() {
  if (isPageContextIncluded.value)
    excludePageContext()
  else
    includePageContext()

  focusDraftInput()
}

function stopThinking() {
  void cancel().catch((error: unknown) => {
    console.error('Failed to cancel Docue Agent work', error)
  })
}

async function clearChat() {
  if (!canClear.value)
    return

  // A run that can't be cancelled (dead dev run, network error) must not block clearing.
  if (hasActiveTurn.value) {
    await cancel().catch((error: unknown) => {
      console.error('Failed to cancel Docue Agent work', error)
    })
  }

  clear()
  resetAttachments()
  inFlightTurn.value = undefined
  focusDraftInput()
}
</script>

<template>
  <div
    class="relative"
    :class="[
      fullScreen ? 'size-full' : 'w-[min(28rem,calc(100vw-1.5rem))] pt-3',
      { 'select-none': isResizing },
    ]"
  >
    <div
      v-if="!fullScreen"
      class="
        absolute inset-x-0 top-0 z-10 flex h-3 cursor-row-resize items-center justify-center
        after:absolute after:inset-x-2 after:top-1/2 after:h-0.5 after:-translate-y-1/2
        after:rounded-full after:bg-transparent after:transition-colors after:duration-200
        hover:after:bg-neutral-300
      "
      :class="{ 'after:bg-neutral-400': isResizing }"
      @pointerdown.prevent="startResize"
    />

    <div
      ref="chatPanel"
      class="relative flex flex-col overflow-hidden bg-white"
      :class="fullScreen
        ? 'size-full'
        : 'rounded-md shadow-[0_-8px_24px_-6px_rgba(0,0,0,0.12),0_2px_8px_-2px_rgba(0,0,0,0.06)] ring-1 ring-neutral-200'"
      :style="fullScreen ? undefined : { height: `${height}px` }"
    >
      <div
        v-if="isOverDropZone && canAttach"
        class="
          pointer-events-none absolute inset-0 z-20 flex items-center justify-center
          bg-neutral-900/40 px-6
        "
        :class="{ 'rounded-md': !fullScreen }"
      >
        <p class="rounded-md bg-white px-3 py-2 text-sm font-medium text-neutral-900 shadow-sm">
          {{ t('docueAgent.attachments.drop') }}
        </p>
      </div>
      <div class="flex shrink-0 items-center gap-2 px-4 py-3">
        <p
          class="min-w-0 font-medium text-gray-900"
          :class="fullScreen ? 'text-base' : 'text-sm'"
        >
          {{ t('docueAgent.title') }}
        </p>
        <UiBadge
          color="info"
          variant="soft"
          aspect="rounded"
        >
          {{ t('docueAgent.beta') }}
        </UiBadge>
        <UiButton
          type="button"
          :size="buttonSize"
          variant="ghost"
          aspect-square
          icon="docue:trash-01"
          class="ml-auto"
          :disabled="!canClear"
          :aria-label="t('docueAgent.clear')"
          @click="clearChat"
        />
        <UiButton
          type="button"
          :size="buttonSize"
          variant="ghost"
          aspect-square
          icon="docue:x-close"
          :aria-label="t('close')"
          @click="emit('close')"
        />
      </div>

      <UiMessageScrollerProvider auto-scroll>
        <div class="relative min-h-0 flex-1">
          <UiMessageScroller>
            <UiMessageScrollerViewport>
              <UiMessageScrollerContent class="gap-4 p-3 sm:p-4">
                <UiMessageScrollerItem
                  v-if="showStarters"
                  message-id="starters"
                  class="flex min-h-full flex-col justify-center gap-4 py-2"
                >
                  <div class="space-y-1">
                    <p class="text-sm font-medium text-gray-900">
                      {{ t('docueAgent.starters.title') }}
                    </p>
                    <p class="text-sm text-gray-500">
                      {{ t('docueAgent.starters.description') }}
                    </p>
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <UiButton
                      v-for="starter in starters"
                      :key="starter.id"
                      type="button"
                      :size="starterButtonSize"
                      color="neutral"
                      variant="outline"
                      class="rounded-full"
                      :icon-leading="starter.icon"
                      @click="fillStarter(starter.prompt)"
                    >
                      {{ starter.label }}
                    </UiButton>
                  </div>
                </UiMessageScrollerItem>

                <UiMessageScrollerItem
                  v-for="message in displayMessages"
                  :key="message.id"
                  :message-id="message.id"
                >
                  <UiMessage :align="message.role === 'user' ? 'end' : 'start'">
                    <UiMessageContent>
                      <AppDocueAgentActivity
                        v-if="
                          message.role === 'assistant'
                            && (message.isWorking || message.durationSeconds !== undefined)
                        "
                        :is-working="message.isWorking"
                        :started-at="message.startedAt"
                      />
                      <UiBubble
                        v-if="message.role === 'user' && message.text.trim()"
                        variant="muted"
                      >
                        <UiBubbleContent class="whitespace-pre-wrap">
                          {{ message.text }}
                        </UiBubbleContent>
                      </UiBubble>
                      <UiBubble
                        v-for="(block, index) in message.role === 'assistant' ? message.textBlocks : []"
                        :key="`${message.id}:text:${block.stepIndex ?? index}`"
                        variant="ghost"
                        class="w-full"
                      >
                        <UiBubbleContent class="w-full">
                          <Comark
                            :markdown="block.text"
                            :components="markdownComponents"
                            :plugins="markdownPlugins"
                            class="
                              prose prose-sm prose-neutral max-w-full overflow-x-auto break-words
                              prose-hr:my-4
                              [&_*:first-child]:mt-0 [&_*:last-child]:mb-0
                            "
                          />
                        </UiBubbleContent>
                      </UiBubble>
                      <AppDocueAgentFeedback
                        v-if="message.feedbackTraceId"
                        :trace-id="message.feedbackTraceId"
                      />
                      <div
                        v-if="message.attachments.length"
                        class="flex max-w-full flex-col gap-1.5"
                        :class="message.role === 'user' ? 'items-end self-end' : 'items-start'"
                      >
                        <AppDocueAgentAttachment
                          v-for="(attachment, index) in message.attachments"
                          :key="`${message.id}:${index}`"
                          :filename="attachment.filename"
                          :media-type="attachment.mediaType"
                          :size="attachment.size"
                        />
                      </div>
                      <div
                        v-if="message.pageContext"
                        class="
                          flex max-w-full items-center gap-1.5 self-end text-xs
                          text-gray-400
                        "
                      >
                        <UiIcon
                          :name="message.pageContext.icon ?? 'docue:target-05'"
                          class="size-4 shrink-0 text-gray-500"
                        />
                        <span class="min-w-0 truncate font-medium text-gray-600">
                          {{ message.pageContext.label }}
                        </span>
                        <span class="shrink-0">
                          {{ t('docueAgent.context.added') }}
                        </span>
                      </div>
                      <div
                        v-if="message.inputPrompts.length"
                        class="flex w-full flex-col gap-3"
                      >
                        <template
                          v-for="prompt in message.inputPrompts"
                          :key="prompt.interactionId"
                        >
                          <AppDocueAgentToolConfirmation
                            v-if="prompt.isToolConfirmation"
                            :interaction-id="prompt.interactionId"
                            :request="prompt.request"
                            :response="prompt.response"
                            :tool-input="prompt.toolInput"
                            :tool-name="prompt.toolName"
                            :disabled="isResuming || !platformAgentUrl || !prompt.pending"
                            @answer="answerInputRequest"
                          />
                          <AppDocueAgentInputRequest
                            v-else
                            :interaction-id="prompt.interactionId"
                            :request="prompt.request"
                            :response="prompt.response"
                            :disabled="isResuming || !platformAgentUrl || !prompt.pending"
                            @answer="answerInputRequest"
                          />
                        </template>
                      </div>
                    </UiMessageContent>
                  </UiMessage>
                </UiMessageScrollerItem>

                <UiMessageScrollerItem
                  v-if="showGenericActivity"
                  message-id="activity"
                >
                  <AppDocueAgentActivity
                    is-working
                    :started-at="activeTurnStartedAt"
                  />
                </UiMessageScrollerItem>

                <UiMessageScrollerItem
                  v-else-if="error || turnFailure || !platformAgentUrl"
                  message-id="error"
                >
                  <UiMarker role="alert">
                    <UiMarkerContent class="text-red-600">
                      {{ t('docueAgent.unavailable') }}
                    </UiMarkerContent>
                  </UiMarker>
                </UiMessageScrollerItem>

                <UiMessageScrollerItem
                  v-if="displayMessages.length > 0 && !isBusy && !hasWorkingAssistantMessage"
                  message-id="disclaimer"
                  class="sm:hidden"
                >
                  <p class="text-center text-xs leading-4 text-gray-400">
                    {{ t('docueAgent.disclaimer-tooltip') }}
                  </p>
                </UiMessageScrollerItem>
                <div
                  v-if="legalResearchStatuses.length && !isResuming"
                  class="shrink-0"
                  :style="{ height: legalResearchInset }"
                  aria-hidden="true"
                />
              </UiMessageScrollerContent>
            </UiMessageScrollerViewport>
            <UiMessageScrollerButton
              :class="{ 'size-11': fullScreen }"
              :style="legalResearchStatuses.length && !isResuming
                ? { bottom: `calc(${legalResearchInset} + 1rem)` }
                : undefined"
            />
          </UiMessageScroller>
          <div
            v-if="legalResearchStatuses.length && !isResuming"
            class="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex flex-col items-start gap-1 px-3 pb-3"
          >
            <p class="px-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-neutral-400">
              {{ t('docueAgent.backgroundTasks') }}
            </p>
            <AppDocueAgentBackgroundTask
              v-for="status in legalResearchStatuses"
              :key="status.id"
              :label="status.phase === 'waiting'
                ? t('docueAgent.waitingForAnswer')
                : t('docueAgent.researchingFinnishLaw')"
              :started-at="status.startedAt"
            />
          </div>
        </div>
      </UiMessageScrollerProvider>

      <form
        class="shrink-0 border-t border-neutral-200"
        @submit.prevent="sendMessage"
      >
        <div
          v-if="hasComposerChips"
          class="flex flex-col gap-1.5 px-3 pt-3"
        >
          <div
            v-if="activePageContext"
            class="
              flex h-8 min-w-0 items-center gap-2 rounded-lg bg-neutral-100 px-2
              text-sm text-neutral-800
            "
          >
            <UiIcon
              :name="activePageContext.icon ?? 'docue:target-05'"
              class="size-4 shrink-0"
            />
            <span class="min-w-0 flex-1 truncate">
              {{ activePageContext.label }}
            </span>
            <UiButton
              type="button"
              size="2xs"
              variant="ghost"
              aspect-square
              icon="docue:x-close"
              class="shrink-0"
              :aria-label="t('docueAgent.context.remove')"
              @click="excludePageContext"
            />
          </div>
          <AppDocueAgentAttachment
            v-for="attachment in pendingAttachments"
            :key="attachment.id"
            :filename="attachment.name"
            :media-type="attachment.mediaType"
            :size="attachment.size"
            removable
            @remove="removePendingAttachment(attachment.id)"
          />
        </div>
        <p
          v-if="attachmentError"
          class="px-3 pt-2 text-xs text-red-600"
          role="alert"
        >
          {{ attachmentError }}
        </p>
        <div
          class="flex items-start gap-2"
          :class="fullScreen ? 'p-3' : undefined"
        >
          <UiTextarea
            ref="draftInput"
            v-model="draft"
            :placeholder="draftPlaceholder"
            :rows="fullScreen ? 1 : 2"
            class="
              max-h-40 min-w-0 flex-1 resize-none overflow-y-auto !rounded-none
              !border-0 !bg-transparent !shadow-none focus-visible:!border-0
              focus-visible:!ring-0
            "
            :class="fullScreen
              ? 'min-h-11 text-base !p-0'
              : 'min-h-16 !p-3'"
            @keydown.enter.exact="submitOnEnter"
            @paste="onComposerPaste"
          />
          <UiTooltip v-if="fullScreen">
            <UiTooltipTrigger as-child>
              <p
                class="
                  hidden max-w-40 shrink-0 cursor-help self-center truncate text-center text-xs
                  text-gray-400 sm:block
                "
                tabindex="0"
              >
                {{ t('docueAgent.disclaimer') }}
              </p>
            </UiTooltipTrigger>
            <UiTooltipContent class="max-w-64 text-center">
              {{ t('docueAgent.disclaimer-tooltip') }}
            </UiTooltipContent>
          </UiTooltip>
          <template v-if="fullScreen">
            <UiButton
              v-if="!freeformInputRequest"
              type="button"
              :size="buttonSize"
              color="neutral"
              variant="ghost"
              aspect-square
              icon="docue:attachment-01"
              class="shrink-0 rounded-full"
              :disabled="!canAttach"
              :aria-label="t('docueAgent.attachments.add')"
              @click="openAttachmentDialog"
            />
            <UiButton
              v-if="canUsePageContext"
              type="button"
              :size="buttonSize"
              color="neutral"
              variant="ghost"
              aspect-square
              icon="docue:target-05"
              class="shrink-0 rounded-full"
              :class="{ 'bg-neutral-100': isPageContextIncluded }"
              :aria-pressed="isPageContextIncluded"
              :aria-label="
                t(isPageContextIncluded
                  ? 'docueAgent.context.exclude'
                  : 'docueAgent.context.include')
              "
              @click="togglePageContext"
            />
            <UiButton
              v-if="showStop"
              type="button"
              :size="buttonSize"
              color="neutral"
              aspect-square
              class="shrink-0 rounded-full"
              :aria-label="t('docueAgent.stop')"
              icon="docue:stop"
              @click="stopThinking"
            />
            <UiButton
              v-else
              type="submit"
              :size="buttonSize"
              color="neutral"
              aspect-square
              class="shrink-0 rounded-full"
              :disabled="!canSend"
              :aria-label="t('docueAgent.send')"
              icon="docue:arrow-up"
            />
          </template>
        </div>
        <div
          v-if="!fullScreen"
          class="flex items-center gap-2 px-3 pb-3"
        >
          <UiTooltip v-if="!freeformInputRequest">
            <UiTooltipTrigger as-child>
              <UiButton
                type="button"
                :size="buttonSize"
                color="neutral"
                variant="ghost"
                aspect-square
                icon="docue:attachment-01"
                class="shrink-0 rounded-full"
                :disabled="!canAttach"
                :aria-label="t('docueAgent.attachments.add')"
                @click="openAttachmentDialog"
              />
            </UiTooltipTrigger>
            <UiTooltipContent>
              {{ t('docueAgent.attachments.add') }}
            </UiTooltipContent>
          </UiTooltip>
          <UiTooltip v-if="canUsePageContext">
            <UiTooltipTrigger as-child>
              <UiButton
                type="button"
                :size="buttonSize"
                color="neutral"
                variant="ghost"
                aspect-square
                icon="docue:target-05"
                class="shrink-0 rounded-full"
                :class="{ 'bg-neutral-100': isPageContextIncluded }"
                :aria-pressed="isPageContextIncluded"
                :aria-label="
                  t(isPageContextIncluded
                    ? 'docueAgent.context.exclude'
                    : 'docueAgent.context.include')
                "
                @click="togglePageContext"
              />
            </UiTooltipTrigger>
            <UiTooltipContent>
              {{
                t(isPageContextIncluded
                  ? 'docueAgent.context.exclude'
                  : 'docueAgent.context.include')
              }}
            </UiTooltipContent>
          </UiTooltip>
          <UiTooltip>
            <UiTooltipTrigger as-child>
              <p
                class="
                  hidden min-w-0 flex-1 cursor-help truncate text-center text-xs text-gray-400
                  sm:block
                "
                tabindex="0"
              >
                {{ t('docueAgent.disclaimer') }}
              </p>
            </UiTooltipTrigger>
            <UiTooltipContent class="max-w-64 text-center">
              {{ t('docueAgent.disclaimer-tooltip') }}
            </UiTooltipContent>
          </UiTooltip>
          <UiButton
            v-if="showStop"
            type="button"
            :size="buttonSize"
            color="neutral"
            aspect-square
            class="ml-auto shrink-0 rounded-full"
            :aria-label="t('docueAgent.stop')"
            icon="docue:stop"
            @click="stopThinking"
          />
          <UiButton
            v-else
            type="submit"
            :size="buttonSize"
            color="neutral"
            aspect-square
            class="ml-auto shrink-0 rounded-full"
            :disabled="!canSend"
            :aria-label="t('docueAgent.send')"
            icon="docue:arrow-up"
          />
        </div>
      </form>
    </div>
  </div>
</template>
